/**
 * DOM side of the consent contract: read/write the bt_consent cookie (localStorage fallback)
 * and remove tracker data on withdrawal. Framework-free. Keep in sync with the web app copy.
 */
import {
  CONSENT_COOKIE_NAME,
  type ConsentChoices,
  type ConsentSource,
  buildConsentCookie,
  decodeConsentCookieValue,
  isCurrentConsent,
  isTrackerCookieName,
  parseStoredConsent,
  serializeStoredConsent,
  TRACKER_STORAGE_KEYS,
  type StoredConsent,
  consentCookieDomain,
} from './contract';
import { currentJurisdiction, defaultChoicesFor, type JurisdictionDecision } from './jurisdiction';

export const CONSENT_CHANGE_EVENT = 'bt-consent-change';
export const CONSENT_OPEN_EVENT = 'bt-consent-open';

/** RFC 4122 v4 UUID. The consent-log endpoint requires a UUID consentId. */
export function newConsentId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const part = document.cookie
      .split(';')
      .map(p => p.trim())
      .find(p => p.startsWith(name + '='));
    return part ? part.slice(name.length + 1) : null;
  } catch {
    return null;
  }
}

/** Cookie first, localStorage fallback (spec section 5 step 2). Returns the stored record, current or not. */
export function readStoredConsent(): StoredConsent | null {
  const fromCookie = readCookie(CONSENT_COOKIE_NAME);
  const decoded = fromCookie ? decodeConsentCookieValue(fromCookie) : null;
  const fromCookieParsed = parseStoredConsent(decoded);
  if (fromCookieParsed) return fromCookieParsed;
  try {
    if (typeof localStorage === 'undefined') return null;
    return parseStoredConsent(localStorage.getItem(CONSENT_COOKIE_NAME));
  } catch {
    return null;
  }
}

/** The stored choice if it is still valid for this banner version, else null. */
export function currentConsent(): StoredConsent | null {
  const stored = readStoredConsent();
  return isCurrentConsent(stored) ? stored : null;
}

/**
 * Effective consent: the stored visitor choice if it is current, otherwise the jurisdiction default
 * (OPT_OUT regions: granted, GPC strips marketing; OPT_IN and unknown: denied). Every reader that
 * gates a tracker must use this, not currentConsent(), or opt-out visitors would never get analytics.
 */
export function effectiveConsent(): { choices: ConsentChoices; stored: boolean; decision: JurisdictionDecision; source: ConsentSource | null } {
  const decision = currentJurisdiction();
  const stored = currentConsent();
  if (stored) return { choices: stored.choices, stored: true, decision, source: null };
  const def = defaultChoicesFor(decision);
  return { choices: def.choices, stored: false, decision, source: def.source };
}

export function hasAnalyticsConsent(): boolean {
  return effectiveConsent().choices.analytics === true;
}

export function hasMarketingConsent(): boolean {
  return effectiveConsent().choices.marketing === true;
}

/** Writes the cookie (shared across blueticks.co and app.blueticks.co) and the localStorage fallback. */
export function writeStoredConsent(c: StoredConsent): void {
  if (typeof document !== 'undefined') {
    try {
      document.cookie = buildConsentCookie(c, window.location.hostname);
    } catch {
      /* Cookies blocked: the choice still applies for this page view. */
    }
  }
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CONSENT_COOKIE_NAME, serializeStoredConsent(c));
  } catch {
    /* Storage denied: the cookie is the only record. */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
  }
}

function expireCookie(name: string, domain?: string): void {
  const base = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  document.cookie = domain ? `${base}; Domain=${domain}` : base;
}

/**
 * Withdrawal: expire every tracker cookie we can see, on the host and on the shared .blueticks.co
 * domain, and clear tracker localStorage keys. Returns the names that were present.
 * HttpOnly cookies cannot be read or expired from script; none of our trackers set those.
 */
export function clearTrackerData(hostname: string): string[] {
  const removed: string[] = [];
  if (typeof document !== 'undefined') {
    const domain = consentCookieDomain(hostname);
    const names = document.cookie
      .split(';')
      .map(p => p.trim().split('=')[0])
      .filter(name => name.length > 0 && isTrackerCookieName(name));
    for (const name of new Set(names)) {
      removed.push(name);
      try {
        expireCookie(name);
        if (domain) expireCookie(name, domain);
      } catch {
        /* ignore */
      }
    }
  }
  try {
    if (typeof localStorage !== 'undefined') {
      for (const key of TRACKER_STORAGE_KEYS) {
        if (localStorage.getItem(key) !== null) {
          localStorage.removeItem(key);
          removed.push(`localStorage:${key}`);
        }
      }
    }
  } catch {
    /* ignore */
  }
  // UTM and click-ID capture is written to sessionStorage (index.html and attribution), so clear it too.
  try {
    if (typeof sessionStorage !== 'undefined') {
      for (const key of TRACKER_STORAGE_KEYS) {
        if (sessionStorage.getItem(key) !== null) {
          sessionStorage.removeItem(key);
          removed.push(`sessionStorage:${key}`);
        }
      }
    }
  } catch {
    /* ignore */
  }
  return removed;
}
